import { styled } from "../shared/Template.styles";
import { TextField, Stack, Box, Snackbar, Alert } from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import { useCreateUser } from "./context/CreateUserHook";
import { ErrorMessage } from "@hookform/error-message";
import { useCreateUserMutation } from "@/src/graphql/generated/api/graphql";
import { useNavigate } from "@tanstack/react-router";
import SelectRole from "@/src/components/organisms/SelectRole/SelectRole";

const CreateUserTemplate = () => {
    const { t } = useTranslation("common", {
        keyPrefix: "create-user",
    });

    const createUser = useCreateUser();

    const [loading, setLoading] = useState(false);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");

    const { form } = createUser;
    const user = form.watch("user");

    const navigate = useNavigate();

    const handleCloseSnackbar = (
        event?: React.SyntheticEvent | Event,
        reason?: string
    ) => {
        if (reason === "clickaway") {
            return;
        }

        setSnackbarOpen(false);
    };

    const onSubmit = form.handleSubmit(
        () => {
            setLoading(true);
            createUserMutation();
        },
        (_error) => {
            setErrorMessage(t("validation-error"));
            setSnackbarOpen(true);
        }
    );

    const [createUserMutation] = useCreateUserMutation({
        variables: {
            input: {
                firstName: user?.firstName,
                lastName: user?.lastName,
                email: user?.email,
                temporaryPassword: user?.password,
                roleId: user?.role,
            },
        },
        refetchQueries: ["GetUsers"],
        onCompleted: () => {
            setLoading(false);
            void navigate({ to: "/admin/users" });
        },
        onError: (_error) => {
            setLoading(false);
            setErrorMessage(t("creation-failed"));
            setSnackbarOpen(true);
        },
    });

    return (
        <>
            <styled.Container>
                <styled.Header title={t("new-user")} />
                <styled.Main>
                    <form onSubmit={onSubmit} data-testid="form">
                        <Stack spacing={4} direction="column">
                            <TextField
                                {...form.register("user.firstName")}
                                id="firstName"
                                label={t("first-name")}
                                error={!!form.formState.errors.user?.firstName}
                                helperText={
                                    <ErrorMessage
                                        errors={form.formState.errors}
                                        name="user.firstName"
                                    />
                                }
                                fullWidth
                            />
                            <TextField
                                {...form.register("user.lastName")}
                                id="lastName"
                                label={t("last-name")}
                                error={!!form.formState.errors.user?.lastName}
                                helperText={
                                    <ErrorMessage
                                        errors={form.formState.errors}
                                        name="user.lastName"
                                    />
                                }
                                fullWidth
                            />
                            <TextField
                                {...form.register("user.email")}
                                id="email"
                                label={t("email")}
                                type="email"
                                error={!!form.formState.errors.user?.email}
                                helperText={
                                    <ErrorMessage
                                        errors={form.formState.errors}
                                        name="user.email"
                                    />
                                }
                                fullWidth
                            />
                            <SelectRole
                                {...form.register("user.role")}
                                label={t("role")}
                            />
                            <TextField
                                {...form.register("user.password")}
                                id="password"
                                label={t("password")}
                                type="password"
                                error={!!form.formState.errors.user?.password}
                                helperText={
                                    <ErrorMessage
                                        errors={form.formState.errors}
                                        name="user.password"
                                    />
                                }
                                fullWidth
                            />
                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent: "flex-end",
                                    mt: 2,
                                }}
                            >
                                <LoadingButton
                                    type="submit"
                                    loading={loading}
                                    variant="contained"
                                    color="primary"
                                    disabled={!form.formState.isValid}
                                    data-testid="submit-button"
                                >
                                    {t("submit")}
                                </LoadingButton>
                            </Box>
                        </Stack>
                    </form>
                </styled.Main>
            </styled.Container>

            <Snackbar
                open={snackbarOpen}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert
                    onClose={handleCloseSnackbar}
                    severity="error"
                    sx={{ width: "100%" }}
                >
                    {errorMessage}
                </Alert>
            </Snackbar>
        </>
    );
};

export default CreateUserTemplate;
